import type { VehicleType, VehicleLayout, SeatData, GenderPreference, SeatSide } from "@/components/SeatMap/seat-map.types"

export const VEHICLE_LAYOUTS: Record<VehicleType, VehicleLayout> = {
  HATCH_SEDAN: { totalSeats: 5, frontSeats: 2, backSeats: 3, backRows: 1 },
  SUV: { totalSeats: 8, frontSeats: 2, backSeats: 6, backRows: 2 },
  VAN: { totalSeats: 8, frontSeats: 2, backSeats: 6, backRows: 2 },
}

export const VEHICLE_LABELS: Record<VehicleType, string> = {
  HATCH_SEDAN: "Hatch / Sedan",
  SUV: "SUV",
  VAN: "Van",
}

export const VEHICLE_ICONS: Record<VehicleType, string> = {
  HATCH_SEDAN: "🚗",
  SUV: "🚙",
  VAN: "🚐",
}

export const GENDER_LABELS: Record<GenderPreference, string> = {
  INDIFERENTE: "Indiferente",
  MASCULINO: "Masculino",
  FEMININO: "Feminino",
}

export function generateSeatsForVehicle(vehicleType: VehicleType, initialAvailable = true): SeatData[] {
  const layout = VEHICLE_LAYOUTS[vehicleType]
  const seats: SeatData[] = []

  seats.push(
    { id: "front_left", row: 0, side: "left", available: false },
    { id: "front_right", row: 0, side: "right", available: initialAvailable },
  )

  if (vehicleType === "HATCH_SEDAN") {
    seats.push(
      { id: "back_left", row: 1, side: "left", available: initialAvailable },
      { id: "back_mid", row: 1, side: "center", available: initialAvailable },
      { id: "back_right", row: 1, side: "right", available: initialAvailable },
    )
  } else if (vehicleType === "SUV") {
    seats.push(
      { id: "back_left", row: 1, side: "left", available: initialAvailable },
      { id: "back_mid", row: 1, side: "center", available: initialAvailable },
      { id: "back_right", row: 1, side: "right", available: initialAvailable },
    )
    seats.push(
      { id: "row3_left", row: 2, side: "left", available: initialAvailable },
      { id: "row3_mid", row: 2, side: "center", available: initialAvailable },
      { id: "row3_right", row: 2, side: "right", available: initialAvailable },
    )
  } else if (vehicleType === "VAN") {
    seats.push(
      { id: "back_left", row: 1, side: "left", available: initialAvailable },
      { id: "back_mid", row: 1, side: "center", available: initialAvailable },
      { id: "back_right", row: 1, side: "right", available: initialAvailable },
    )
    seats.push(
      { id: "row3_left", row: 2, side: "left", available: initialAvailable },
      { id: "row3_mid", row: 2, side: "center", available: initialAvailable },
      { id: "row3_right", row: 2, side: "right", available: initialAvailable },
    )
  }

  return seats
}

export function getSeatRow(seatId: string): "front" | "back" {
  return seatId.startsWith("front") ? "front" : "back"
}

export function countAvailableSeats(seats: SeatData[]): number {
  return seats.filter((s) => s.available && !s.id.startsWith("front")).length
}

export function canSeatAcceptPassenger(
  seat: SeatData,
  passengerGender: GenderPreference,
  policy: GenderPreference
): boolean {
  if (!seat.available) return false
  if (policy === "INDIFERENTE") return true
  return policy === passengerGender
}

export function isGenderPreference(v: unknown): v is GenderPreference {
  return v === "INDIFERENTE" || v === "MASCULINO" || v === "FEMININO"
}

/**
 * Normaliza políticas de gênero antigas ({ front, back }) para o formato por
 * fileira ({ "0", "1", "2" }). Mantém valores numéricos existentes.
 */
export function normalizeGenderPolicy(
  policy: unknown
): Record<string, GenderPreference> {
  const out: Record<string, GenderPreference> = {}

  if (policy && typeof policy === "object" && !Array.isArray(policy)) {
    const p = policy as Record<string, unknown>

    if (isGenderPreference(p.front)) out["0"] = p.front
    if (isGenderPreference(p.back)) {
      const b = p.back
      if (!isGenderPreference(out["1"])) out["1"] = b
      if (!isGenderPreference(out["2"])) out["2"] = b
    }

    for (const k of Object.keys(p)) {
      if (k === "front" || k === "back") continue
      if (/^\d+$/.test(k) && isGenderPreference(p[k])) {
        out[k] = p[k] as GenderPreference
      }
    }
  }

  return out
}

export function genderPreferenceForRow(
  policy: unknown,
  row: number
): GenderPreference {
  const n = normalizeGenderPolicy(policy)
  const v = n[String(row)]
  return isGenderPreference(v) ? v : "INDIFERENTE"
}

/**
 * Conflito da política de gênero com a preferência de uma busca.
 *
 * `INDIFERENTE` (do lado da busca ou de qualquer fileira) nunca conflita.
 * Qualquer fileira restrita a OUTRO gênero conflita — bloqueio conservador da
 * carona inteira, a mesma semântica do filtro `.back` original (HEAD), agora
 * cobrindo formato por fileira E legado `{front, back}`.
 */
export function genderPolicyConflictsWith(
  policy: unknown,
  preference: GenderPreference
): boolean {
  if (preference === "INDIFERENTE") return false
  return Object.values(normalizeGenderPolicy(policy)).some(
    (v) => v !== "INDIFERENTE" && v !== preference
  )
}

export function seatRowLabel(row: number): string {
  if (row === 0) return "Banco da frente"
  if (row === 1) return "1ª fileira de trás"
  if (row === 2) return "2ª fileira de trás"
  return `Fileira ${row}`
}

export function createDefaultGenderPolicy(): Record<string, GenderPreference> {
  return {}
}
